import React, { useEffect, useRef, useState, memo } from "react";
import styled from "@emotion/styled";
import { MerchantSelector, setMerchantIsRequired } from "../../../Merchants";
import { CardNumber } from "../CardNumber";
import { CardPin } from "../CardPin";
import { CardBalance } from "../Balance";
import { RefId } from "../Ref";
import { PrimaryButton, SecondaryButton } from "../../../Button";
import { Icon } from "ccui-framework";
import { useDispatch, useSelector } from "react-redux";
import { addCard } from "../../thunk";
import { LOADING_EMUNS } from "../../constants";
import { setLoadingState } from "../../action";
import PropTypes from "prop-types";
import { PricingDetails } from "../../../Merchants/PricingDetails";

const Container = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 1rem;
  @media (max-width: 1200px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }
`;

const ErrorContainer = styled.div`
  margin-left: 10px;
`;

const SubmitButton = styled(PrimaryButton)`
  width: 50px;
  height: 50px;
  margin: 0 10px;
  @media (max-width: 1200px) {
    width: 100%;
    height: 44px;
    margin: 0;
  }
`;

const RemoveButton = styled(SecondaryButton)`
  width: 50px;
  height: 50px;
  margin: 0 10px;
`;

const Create = memo(({ enteredCard, idx, canRemove, doRemove, isEditCard }) => {
  const dispatch = useDispatch();
  const merchantSelectorRef = useRef(null);
  const formRef = useRef(null);
  const initCard = {
    selectedMerchant: null,
    balance: "",
    refId: "",
    pin: "",
    number: "",
  };
  const initEnteredCard = {
    selectedMerchant: enteredCard?.selectedMerchant ?? null,
    balance: enteredCard?.balance ?? "",
    refId: enteredCard?.refId ?? "",
    pin: enteredCard?.pin ?? "",
    number: enteredCard?.number ?? "",
  };
  const myCardLoadingState = useSelector(
    (state) => state.Cart.loading.state[idx]
  );
  const addCardError = useSelector((state) => state.Cart.error.add[idx]);
  const [myCard, setMyCard] = useState(initEnteredCard);
  const [myCardErr, setMyCardErr] = useState({ error: false, msg: {} });
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const cardErrsToDisplay = [];

  useEffect(() => {
    dispatch(setLoadingState(idx, LOADING_EMUNS.DEFAULT));
  }, []);

  useEffect(() => {
    setMyCard(initEnteredCard);
  }, [enteredCard]);

  useEffect(() => {
    setIsSubmitDisabled(
      myCard.selectedMerchant &&
        myCard.balance &&
        myCard.number &&
        !myCardErr.error
        ? false
        : true
    );
  }, [myCard]);

  useEffect(() => {
    if (myCardLoadingState === LOADING_EMUNS.LOADED) {
      dispatch(setLoadingState(idx, LOADING_EMUNS.DEFAULT));
    }

    if (!isEditCard && myCardLoadingState === LOADING_EMUNS.LOADED) {
      formRef.current?.reset();
      setMyCard(initCard);
    }
  }, [isEditCard, myCardLoadingState]);

  const merchantSelectorFocus = () => {
    merchantSelectorRef.current?.focus();
    setError("merchant", "Merchant is required.");
  };

  const setError = (field, message) => {
    if (message) {
      setMyCardErr((err) => ({
        error: true,
        msg: { ...err.msg, [field]: message },
      }));
    }
  };

  const resetEnteredCardError = () => {
    if (enteredCard.error) enteredCard.error = "";
  };

  const resetErrors = (field) => {
    if (myCardErr.msg.hasOwnProperty(field)) {
      setMyCardErr((err) => {
        delete err.msg[field];
        return {
          error: Object.keys(err.msg).length === 0 ? false : true,
          msg: { ...err.msg },
        };
      });
    }
  };

  const submit = async (e) => {
    e.preventDefault();

    if (!myCardErr.error) {
      dispatch(
        addCard(idx, {
          card: {
            merchantId: myCard.selectedMerchant.id,
            enterValue: Number(myCard.balance),
            number: myCard.number,
            pin: myCard.pin,
            refId: myCard.refId,
          },
        })
      );
    }
  };

  Object.entries(myCardErr.msg).forEach((error, i) => {
    cardErrsToDisplay.push(
      <p className="errorMsg" key={i}>
        {error[1]}
      </p>
    );
  });

  return (
    <form ref={formRef}>
      <Container>
        <MerchantSelector
          ref={merchantSelectorRef}
          defaultValue={myCard.selectedMerchant}
          onChange={(merchant) => {
            resetEnteredCardError();

            if (merchant === null) {
              if (!isEditCard) {
                setMyCard(initCard);
                formRef.current?.reset();
              } else {
                setMyCard((card) => ({
                  ...card,
                  selectedMerchant: initCard.selectedMerchant,
                }));
              }
              resetErrors("merchant");
            } else {
              dispatch(setMerchantIsRequired(false));
              setMyCard((card) => ({ ...card, selectedMerchant: merchant }));
              resetErrors("merchant");
            }
          }}
        />
        <CardNumber
          merchantSelectorFocus={merchantSelectorFocus}
          hasMerchant={!!myCard.selectedMerchant}
          defaultValue={myCard.number}
          validation={myCard?.selectedMerchant?.validation ?? ""}
          onChange={(field, value, error, msg) => {
            resetEnteredCardError();

            if (error) {
              setError(field, msg);
            } else {
              if (value) {
                setMyCard((card) => ({ ...card, [field]: value }));
                resetErrors(field);
              }
            }
          }}
        />
        <CardPin
          merchantSelectorFocus={merchantSelectorFocus}
          hasMerchant={!!myCard.selectedMerchant}
          defaultValue={myCard.pin}
          validation={myCard?.selectedMerchant?.validation ?? ""}
          onChange={(field, value, error, msg) => {
            resetEnteredCardError();

            if (error) {
              setError(field, msg);
            } else {
              if (value) {
                setMyCard((card) => ({ ...card, [field]: value }));
                resetErrors(field);
              }
            }
          }}
        />
        <CardBalance
          merchantSelectorFocus={merchantSelectorFocus}
          hasMerchant={!!myCard.selectedMerchant}
          defaultValue={myCard.balance}
          min={myCard?.selectedMerchant?.limit?.min ?? 0}
          max={myCard?.selectedMerchant?.limit?.max ?? 0}
          onChange={(field, value, error, msg) => {
            resetEnteredCardError();

            if (error) {
              setError(field, msg);
            } else {
              if (value) {
                setMyCard((card) => ({ ...card, [field]: value }));
                resetErrors(field);
              }
            }
          }}
        />
        <RefId
          merchantSelectorFocus={merchantSelectorFocus}
          hasMerchant={!!myCard.selectedMerchant}
          defaultValue={myCard.refId}
          onChange={(e) => {
            resetEnteredCardError();

            const value = e.target.value.trim();
            setMyCard((card) => ({ ...card, refId: value }));
          }}
        />
        <SubmitButton onClick={submit} disabled={isSubmitDisabled}>
          {myCardLoadingState === LOADING_EMUNS.DEFAULT && (
            <Icon name="fa-plus" />
          )}
          {myCardLoadingState === LOADING_EMUNS.ERROR && (
            <Icon name="fa-plus" />
          )}
          {myCardLoadingState === LOADING_EMUNS.LOADING && (
            <Icon name="fa-spinner" />
          )}
        </SubmitButton>
        {enteredCard && canRemove && (
          <RemoveButton
            onClick={(e) => {
              e.preventDefault();
              doRemove();
            }}
          >
            <Icon name="fa-trash-alt" />
          </RemoveButton>
        )}
      </Container>
      <PricingDetails
        merchant={myCard.selectedMerchant}
        balance={myCard.balance}
        invalidBalance={!!myCardErr.msg.balance}
      />
      <ErrorContainer>
        {enteredCard.error && !myCardErr.error && !addCardError && (
          <p className="errorMsg">{enteredCard.error}</p>
        )}
        {myCardErr.error && cardErrsToDisplay}
        {myCardLoadingState === LOADING_EMUNS.ERROR && (
          <p className="errorMsg">{addCardError}</p>
        )}
      </ErrorContainer>
    </form>
  );
});

Create.defaultProps = {
  enteredCard: {},
  idx: "0",
  canRemove: false,
  isEditCard: false,
};

Create.propTypes = {
  idx: PropTypes.string.isRequired,

  enteredCard: PropTypes.object,
  canRemove: PropTypes.bool,
  doRemove: PropTypes.func,
  isEditCard: PropTypes.bool,
};

export { Create };
